// src/components/vehicles/FileUploading/UploadAsset.tsx

import React from "react";
import {
  Button,
  Typography,
  Box,
  Paper,
  Container,
  Divider,
  Stack,
  Snackbar,
  Alert,
  CircularProgress
} from "@mui/material";
import { ArrowBack, CloudUpload, Save } from "@mui/icons-material";
import { styled } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import { Vehicle } from "../../../state/types"; // Adjust path to your types
import ImageOrderList from "./ImageOrderList"; // Make sure this component is also updated
import { useUploadAsset, UploadState } from "../../../hooks/vehicles/useUploadAsset"; // The hook we created

/**
 * Defines the props for the UploadAsset component.
 */
interface UploadAssetProps {
  /** The title displayed at the top of the component. */
  title: string;
  /**
   * The initial vehicle object. Used for editing an existing asset.
   * If not provided, the component will start in a "new asset" state.
   */
  initialVehicle?: Partial<Vehicle>;
  /**
   * Callback for when the component is used in a controlled manner (e.g., a modal).
   * It receives the latest state whenever images are added or reordered.
   */
  onStateChange?: (state: UploadState) => void;
  /**
   * Callback for handling the save action. Required when the component
   * manages its own save button (e.g., on a standalone edit page).
   */
  onSave?: (state: UploadState) => Promise<void>;
  /** If true, the Back and Save buttons are hidden. Useful for embedding in a modal. */
  isEmbedded?: boolean;
}

/**
 * A visually hidden input element used for the file upload button,
 * allowing for complete style control of the button itself.
 */
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * A reusable component for uploading and managing asset media. It can be
 * used for both creating new assets and editing existing ones.
 */
const UploadAsset: React.FC<UploadAssetProps> = ({
  title,
  initialVehicle,
  onStateChange,
  onSave,
  isEmbedded = false
}) => {
  const navigate = useNavigate();

  // The component's logic and state are entirely managed by this custom hook.
  const {
    vehicle,
    currentImageType,
    isSaving,
    notification,
    setCurrentImageType,
    handleFileUpload,
    handleVehicleChange,
    handleCloseNotification,
    handleSave,
  } = useUploadAsset({
    initialVehicle,
    onStateChange,
    onSave,
  });

  const isUploadDisabled = !currentImageType;
  const hasChanges = true; // Simplified for now; you can add more complex logic in the hook if needed.

  const content = (
    <>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        {!isEmbedded && (
          <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            Back
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            {title}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {vehicle.assetIdentifier}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Button
          component="label"
          variant="contained"
          disabled={isUploadDisabled}
          startIcon={<CloudUpload />}
          size="large"
        >
          Upload {currentImageType ? currentImageType.replace('image', '') : 'Files'}
          <VisuallyHiddenInput type="file" onChange={handleFileUpload} accept="image/*,.heic,.heif" />
        </Button>
        {isUploadDisabled && (
          <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
            Please select an image category below to enable upload.
          </Typography>
        )}
      </Stack>

      <Box sx={{ mb: 4 }}>
        <ImageOrderList
          vehicle={vehicle}
          onChange={handleVehicleChange}
          // Pass the uploaded files from the hook to the list for previewing
          uploadedFiles={[]} // This logic is now handled inside ImageOrderList based on `vehicle` state
          setImageType={setCurrentImageType}
        />
      </Box>

      {!isEmbedded && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );

  // If the component is embedded, render it directly without extra padding/containers.
  // Otherwise, wrap it in a standard page layout.
  return isEmbedded ? (
    <Box>{content}</Box>
  ) : (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        {content}
      </Paper>
    </Container>
  );
};

export default UploadAsset;